import React, { useEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { PageHero, Reveal, SectionTitle, Kicker } from '../components/common';
import { img, news as mockNews } from '../mock/mock';
import { api, mediaUrl } from '../lib/api';

const fallbackImg = img.formalGarden;

const News = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get('/posts')
      .then((res) => {
        if (res.data && res.data.length) {
          setItems(res.data.map((p) => ({
            id: p.id,
            title: p.title,
            date: p.date,
            tag: p.tag,
            excerpt: p.excerpt,
            body: p.body,
            image: p.image_url ? mediaUrl(p.image_url) : fallbackImg,
          })));
        } else {
          setItems(mockNews.map((n, i) => ({ id: `m${i}`, ...n, body: '', image: img[n.img] })));
        }
      })
      .catch(() => setItems(mockNews.map((n, i) => ({ id: `m${i}`, ...n, body: '', image: img[n.img] }))));
  }, []);

  if (items.length === 0) {
    return <PageHero image={img.formalGarden} kicker="In The News" title="Stories from the growing garden" sub="Announcements, milestones and moments as the Roderick Gay Botanical Garden takes shape." />;
  }

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div>
      <PageHero image={img.formalGarden} kicker="In The News" title="Stories from the growing garden" sub="Announcements, milestones and moments as the Roderick Gay Botanical Garden takes shape." />

      {/* Featured */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-10 items-center bg-white rounded-2xl overflow-hidden shadow-xl border border-sand">
              <div className="h-72 lg:h-full">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 lg:p-12">
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-3 py-1 rounded-full bg-gold/15 text-gold font-600">{featured.tag}</span>
                  <span className="text-ink/50">{featured.date}</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-forest leading-tight mt-5">{featured.title}</h2>
                <p className="mt-4 text-ink/65 leading-relaxed">{featured.excerpt}</p>
                <button onClick={() => setActive(featured)} className="mt-6 inline-flex items-center gap-2 text-forest font-600 hover:text-gold transition-colors">Read the story <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Grid */}
      {rest.length > 0 && (
        <section className="pb-24 bg-cream">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal><Kicker>More Stories</Kicker><h2 className="font-display text-3xl text-forest mt-3 mb-10">Latest updates</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {rest.map((n, i) => (
                <Reveal key={n.id} delay={i * 100}>
                  <article className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:-translate-y-1.5 transition-transform duration-300 h-full flex flex-col cursor-pointer" onClick={() => setActive(n)}>
                    <div className="h-52 overflow-hidden">
                      <img src={n.image} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="px-3 py-1 rounded-full bg-gold/15 text-gold font-600">{n.tag}</span>
                        <span className="text-ink/50">{n.date}</span>
                      </div>
                      <h3 className="font-display text-xl text-forest mt-4 leading-snug group-hover:text-gold transition-colors">{n.title}</h3>
                      <p className="text-ink/60 text-sm mt-3 leading-relaxed flex-1">{n.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-forest-light text-sm font-600">Read more <ArrowRight className="w-3.5 h-3.5" /></span>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press CTA */}
      <section className="py-16 bg-forest text-cream">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl text-white">Members of the press</h3>
            <p className="text-cream/75 mt-2 text-sm">For media inquiries and our press kit, reach our communications team.</p>
          </div>
          <a href="/contact" className="px-7 py-3.5 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light transition-colors">Contact Press</a>
        </div>
      </section>

      {/* Article reader modal */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-forest-dark/90 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setActive(null)}>
          <div className="bg-cream rounded-2xl max-w-2xl w-full my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
              <button onClick={() => setActive(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-forest"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-3 text-xs">
                <span className="px-3 py-1 rounded-full bg-gold/15 text-gold font-600">{active.tag}</span>
                <span className="text-ink/50">{active.date}</span>
              </div>
              <h2 className="font-display text-3xl text-forest mt-4">{active.title}</h2>
              <p className="mt-4 text-ink/70 leading-relaxed">{active.excerpt}</p>
              {active.body ? (
                <div className="mt-4 text-ink/70 leading-relaxed whitespace-pre-line">{active.body}</div>
              ) : (
                <p className="mt-4 text-ink/45 text-sm italic">Full article coming soon.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
